import {
  Box, Container, Heading, VStack, Card, CardBody, CardHeader, Text,
  SimpleGrid, HStack, Icon, Input, InputGroup, InputLeftElement, Link,
  Tooltip, Center, Button, IconButton, Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import { MdBusiness, MdChevronRight, MdSearch, MdAdminPanelSettings, MdAdd, MdMoreVert, MdEdit, MdDelete } from 'react-icons/md';
import { LoadingSpinner } from '@/design-system/components/LoadingSpinner';
import { EmptyState } from '@/design-system/components/EmptyState';
import { Header } from '@/client/components/layout/Header';
import { OrganizationModal } from '@/modules/auth/components/OrganizationModal';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { useOrgsPage } from '../hooks';

export const OrgsPage: React.FC = () => {
  const p = useOrgsPage();

  if (p.isLoading) {
    return <Box minH="100vh" bg="bg-canvas"><Header /><LoadingSpinner message="Loading organizations..." /></Box>;
  }

  if (p.error) {
    return (
      <Box minH="100vh" bg="bg-canvas">
        <Header />
        <Center minH="calc(100vh - 80px)"><Text color="red.500">Error loading organizations: {(p.error as Error).message}</Text></Center>
      </Box>
    );
  }

  if (!p.data?.items || p.data.items.length === 0) {
    return (
      <Box minH="100vh" bg="bg-canvas">
        <Header />
        <Box minH="calc(100vh - 80px)" display="flex" alignItems="center">
          <Container maxW="4xl">
            <EmptyState icon={<Icon as={MdBusiness} boxSize={16} />} title="No organizations found" description="You don't have access to any organizations yet. Please contact your administrator." />
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <PageContainer
      title="Select Organization"
      description="Choose an organization to access its dashboard"
      action={
        p.isSuperAdmin ? (
          <Button leftIcon={<Icon as={MdAdd} />} colorScheme="brand" onClick={() => p.handleModalOpen('add')} size="lg">Add Organization</Button>
        ) : undefined
      }
    >
      {p.data?.items && p.data.items.length > 0 && (
        <Box>
          <InputGroup size="lg">
            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
            <Input placeholder="Search organizations by name, email, or location..." value={p.searchQuery} onChange={(e) => p.setSearchQuery(e.target.value)} bg="bg-surface" borderColor="border-muted" _hover={{ borderColor: 'border-hover' }} _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }} />
          </InputGroup>
          {p.searchQuery && <Text fontSize="sm" color="gray.600" mt={2}>Found {p.filteredOrganizations.length} organization{p.filteredOrganizations.length !== 1 ? 's' : ''}</Text>}
        </Box>
      )}

      {p.filteredOrganizations.length > 0 ? (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {p.filteredOrganizations.map((org) => (
            <Card key={org.id} data-testid={`org-card-${org.id}`} variant="outline" _hover={{ shadow: 'md', borderColor: 'border-brand-light' }} transition="all 0.2s" cursor="pointer" bg="bg-surface" onClick={() => p.handleSelectOrg(org)} bgGradient={p.cardGradient}>
              <CardHeader pb={2}>
                <HStack justify="space-between">
                  <HStack spacing={3}>
                    <Icon as={MdBusiness} boxSize={6} color="brand.500" />
                    <Heading size="md">{org.name}</Heading>
                  </HStack>
                  <HStack spacing={2}>
                    {p.isSuperAdmin && (
                      <>
                        <Tooltip label="Super Admin Dashboard" placement="top">
                          <Link onClick={(e) => p.handleSuperAdminDashboard(e, org)} color="brand.500" _hover={{ color: 'brand.600' }} display="flex" alignItems="center">
                            <Icon as={MdAdminPanelSettings} boxSize={6} />
                          </Link>
                        </Tooltip>
                        <Menu>
                          <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" onClick={(e) => e.stopPropagation()} aria-label="Organization actions" data-testid={`org-actions-btn-${org.id}`} />
                          <MenuList>
                            <MenuItem icon={<MdEdit />} onClick={(e) => { e.stopPropagation(); p.handleModalOpen('edit', org); }} data-testid={`org-edit-btn-${org.id}`}>Edit Organization</MenuItem>
                            <MenuItem icon={<MdDelete />} onClick={(e) => p.handleDeleteClick(e, org)} color="red.500" data-testid={`org-delete-btn-${org.id}`}>Delete Organization</MenuItem>
                          </MenuList>
                        </Menu>
                      </>
                    )}
                    <Icon as={MdChevronRight} color="gray.400" />
                  </HStack>
                </HStack>
              </CardHeader>
              <CardBody pt={2}>
                <VStack align="start" spacing={1}>
                  {org.email && <Text fontSize="sm" color="gray.400">{org.email}</Text>}
                  {org.city && org.country && <Text fontSize="sm" color="gray.400">{org.city}, {org.country}</Text>}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      ) : (
        <EmptyState icon={<Icon as={MdSearch} boxSize={12} />} title="No organizations found" description={`No organizations match "${p.searchQuery}". Try a different search term.`} action={{ label: 'Clear Search', onClick: () => p.setSearchQuery('') }} />
      )}

      <OrganizationModal
        isOpen={p.isModalOpen}
        onClose={p.onModalClose}
        onSubmit={p.handleSubmit}
        initialData={p.editingOrg ? {
          name: p.editingOrg.name, email: p.editingOrg.email,
          country_code: p.editingOrg.country_code,
          default_country_code: p.editingOrg.default_country_code || '+91',
          time_zone: p.editingOrg.time_zone || 'Asia/Calcutta',
          day_start: p.editingOrg.day_start || '05:30',
          phone_number: p.editingOrg.phone_number.replace(p.editingOrg.country_code, ''),
          dashboard_access: p.editingOrg.dashboard_access || false,
          feature_access: p.editingOrg.feature_access || ['reports'],
          demo_mode: p.editingOrg.demo_mode || false,
          demo_otp: p.editingOrg.demo_otp,
        } : undefined}
        isEditing={!!p.editingOrg}
      />

      <ConfirmDialog
        isOpen={p.isDeleteOpen} onClose={p.onDeleteClose} onConfirm={p.handleDeleteConfirm}
        title="Delete Organization" message={`Are you sure you want to delete "${p.deletingOrg?.name}"? This action cannot be undone.`}
        confirmText="Delete" cancelText="Cancel" isLoading={p.isPendingDelete} colorScheme="red"
      />
    </PageContainer>
  );
};

export default OrgsPage;
